import React, { memo } from 'react';
import { TextWithMentions } from '../TextWithMentions';
import { PencilIcon } from '../Icons/PencilIcon';
import { AttachmentIcon } from '../Icons';
import { useViewerAttachmentCarousel } from '../../hooks';
import type {
  CommentMention,
  CommentAttachment
} from '../../containers/CommentList/CommentList.types';
import type { IconOverrides } from '../../types/IconOverrides';
import type { MentionTooltipDisplayMode } from '../CommonTooltip/MentionTooltip/MentionTooltip.types';
import './CommentViewMode.css';

interface CommentViewModeProps {
  text: string;
  canEdit: boolean;
  onEditStart: () => void;
  commentMentions?: CommentMention[];
  tooltipStyle?: React.CSSProperties;
  iconOverrides?: IconOverrides;
  displayMode?: MentionTooltipDisplayMode;
  /** Saved attachments to show; clicking paperclip opens carousel (File[] or CommentAttachment[]). */
  attachments?: (CommentAttachment | File)[];
}

const CommentViewModeComponent: React.FC<CommentViewModeProps> = ({
  text,
  canEdit,
  onEditStart,
  commentMentions,
  tooltipStyle,
  iconOverrides,
  displayMode,
  attachments
}) => {
  const viewerCarousel = useViewerAttachmentCarousel(attachments);

  return (
    <div className="comment-text__content">
      <TextWithMentions
        text={text}
        commentMentions={commentMentions}
        tooltipStyle={tooltipStyle}
        displayMode={displayMode}
      />
      {viewerCarousel.hasAttachments && (
        <button
          type="button"
          className="comment-text__attachments-wrap comment-text__attachments-trigger"
          onClick={viewerCarousel.openCarousel}
          aria-label={`${viewerCarousel.attachmentCount} attachment(s); view`}
          title="View attachments"
        >
          <AttachmentIcon />
          <span className="comment-text__attachments-badge" aria-hidden>
            {viewerCarousel.attachmentCount > 99
              ? '99+'
              : viewerCarousel.attachmentCount}
          </span>
        </button>
      )}
      {canEdit && (
        <button
          onClick={onEditStart}
          className="comment-text__edit-icon"
          style={iconOverrides?.pencilIconStyles}
          aria-label="Edit comment"
        >
          {iconOverrides?.pencilIcon || <PencilIcon />}
        </button>
      )}
      {viewerCarousel.carouselElement}
    </div>
  );
};

export const CommentViewMode = memo(CommentViewModeComponent);
